import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcRegistrationV1AccountQueryServiceAccountGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-account-query-service-account-get';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { RequiresAccess } from '@interxfi/design-system/access-context';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import type { IxcRegistrationV1AccountState } from '@interxfi/types/generated/api';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../components/router/router-tabbed-navigation';
import { getAccountRiskProfile } from '../../../../../../../../../features/registration/account/account-registration-utils';
import { EditAccountRiskProfileDrawer } from '../../../../../../../../../features/registration/account/edit-account-risk-profile-drawer';
import { getRegistrationRiskProfileListItems } from '../../../../../../../../../features/registration/account/registration-account-list-items';
import { RegistrationAccountOverviewTabbedNavigation } from '../../../../../../../../../features/registration/account/registration-account-overview-tabbed-navigation';
import { getPageHead } from '../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_overview/detail/risk')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-${params.accountId}-risk`,
        label: 'Account Details',
        to: `/registration/account/${params.accountId}/detail/risk`,
      }),
    }) satisfies AggregateBreadcrumb,
  staticData: {
    tab: {
      group: TabbedNavigationGroup.RegistrationAccountOverview,
      label: 'Risk Profile',
      order: 2,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.RegistrationAccountOverview>,
  head: () => getPageHead('Account Risk Profile'),
  component: AccountRiskDetails,
});

function getListItems(state: IxcRegistrationV1AccountState | undefined) {
  const items = getRegistrationRiskProfileListItems(getAccountRiskProfile(state?.data));

  return [items.liquidityNeeds, items.investmentHorizonYears, items.objectives, items.riskTolerance, items.percentageOfAllInvestments];
}

function AccountRiskDetails() {
  const { accountId } = Route.useParams();
  const { data, isPending, error } = useIxcRegistrationV1AccountQueryServiceAccountGet({ accountId });
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.account), [data?.account]);

  return (
    <VerticalStack gap={7}>
      <RegistrationAccountOverviewTabbedNavigation
        accountId={accountId}
        actions={
          <RequiresAccess write>
            <EditAccountRiskProfileDrawer accountId={accountId} trigger={<Button size="small">Edit</Button>} />
          </RequiresAccess>
        }
      />

      <AppReadonlyList isPending={showPending} items={listItems} />
    </VerticalStack>
  );
}
